import {
  Grid,
  CardHeader,
  CardContent,
  Box,
  TextField,
  IconButton,
  MenuItem,
  FormHelperText,
  Typography,
  Paper,
  Chip,
  Alert,
  Stack,
  Card,
  CardContent as CardContentInner,
} from "@mui/material";
import { Form, FormikProvider, useFormik, FieldArray } from "formik";
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  AddCircle,
  RemoveCircle,
  SportsSoccer,
  Business,
  Schedule,
  AttachMoney,
  Settings,
  EmojiEvents,
} from "@mui/icons-material";
import {
  contestServices,
  gameManagementApi,
  getLeagueRoundsWithMatches,
  LeagueManagementService,
} from "../../services/AllServices";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import errorHandler from "../../utils/errorHandler";
import LoadingButton from "@mui/lab/LoadingButton";
import { ContestSchema } from "../../utils/Schema";
import CardLayout from "../../layouts/CardLayout";
import { toast } from "react-toastify";
import { DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import "./MatchSelection.css";

function AddEditContestManagement() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id } = useParams();
  const [data, setData] = useState();
  const [leagues, setLeagues] = useState([]);
  const [country, setCountry] = useState([]);
  const [loading, setLoading] = useState(false);
  const [percentageErrorIndex, setPercentageErrorIndex] = useState(null);
  const [matchCount, setMatchCount] = useState(0);
  const [positionActionsMap, setPositionActionsMap] = useState({});
  const [actions, setActions] = useState([]);
  const [gameWeekIdMap, setGameWeekIdMap] = useState({});
  const [validationContext, setValidationContext] = useState({
    gameType: "",
    numberOfWinners: "",
  });
  const [availableMatches, setAvailableMatches] = useState([]);
  const [selectedMatches, setSelectedMatches] = useState({
    daily: [],
    weekly: {},
    monthly: {},
    season: {},
  });
  const [activeWeekTab, setActiveWeekTab] = useState(1);
  const [currentWeekPage, setCurrentWeekPage] = useState(1);
  const [weeksPerPage] = useState(12);
  const [isFetchingMatches, setIsFetchingMatches] = useState(false);
  const [rounds, setRounds] = useState([]);
  const lastFetchRef = useRef({
    timeRange: "",
    startTime: "",
    endTime: "",
    leagueId: "",
  });
  const debounceTimerRef = useRef(null);

  const fetchCountry = async () => {
    try {
      const response = await LeagueManagementService.getCountry();
      if (response.status == 200) {
        setCountry(response?.data?.data || []);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Error in fetching Country."
      );
    }
  };

  useEffect(() => {
    fetchCountry();
  }, []);

  const fetchContestData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await contestServices.getById({ id });
        const contestData = response?.data?.data;
        setData(contestData);
        if (contestData?.gameWeeks) {
          const selected = {
            daily: [],
            weekly: {},
            monthly: {},
            season: {},
          };
          contestData.gameWeeks.forEach((week) => {
            const matchIds = week?.matches?.map((m) => m?.matchId) || [];
            const weekKey = week.gameWeekId || week._id || week.id;
            if (contestData.gameDuration.type === "daily") {
              selected.daily = selected.daily.concat(matchIds);
            } else if (contestData.gameDuration.type === "week") {
              const weekNumber = Object.keys(selected.weekly).length + 1;
              const uiWeekKey = `week${weekNumber}`;
              selected.weekly[uiWeekKey] = matchIds;
              setGameWeekIdMap((prev) => ({
                ...prev,
                [uiWeekKey]: weekKey,
              }));
            } else if (contestData.gameDuration.type === "monthly") {
              const weekNumber = Object.keys(selected.monthly).length + 1;
              const uiWeekKey = `week${weekNumber}`;
              selected.monthly[uiWeekKey] = matchIds;
              setGameWeekIdMap((prev) => ({
                ...prev,
                [uiWeekKey]: weekKey,
              }));
            } else if (contestData.gameDuration.type === "season") {
              const weekNumber = Object.keys(selected.season).length + 1;
              const uiWeekKey = `week${weekNumber}`;
              selected.season[uiWeekKey] = matchIds;
              setGameWeekIdMap((prev) => ({
                ...prev,
                [uiWeekKey]: weekKey,
              }));
            }
          });
          setSelectedMatches(selected);
        }
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }
    fetchCountry();
  }, [id]);

  useEffect(() => {
    if (data?.gameWeeks) {
      const map = {};
      data.gameWeeks.forEach((week, idx) => {
        const key = `week${idx + 1}`;
        map[key] = week.gameWeekId || week._id || week.id || key;
      });
      setGameWeekIdMap(map);
      if (data.gameDuration?.type !== "daily" && data.gameWeeks.length > 0) {
        const selected = {
          daily: [],
          weekly: {},
          monthly: {},
          season: {},
        };
        data.gameWeeks.forEach((week, idx) => {
          const matchIds = week?.matches?.map((m) => m?.matchId) || [];
          const weekKey = `week${idx + 1}`;
          if (data.gameDuration.type === "week") {
            selected.weekly[weekKey] = matchIds;
          } else if (data.gameDuration.type === "monthly") {
            selected.monthly[weekKey] = matchIds;
          } else if (data.gameDuration.type === "season") {
            selected.season[weekKey] = matchIds;
          }
        });
        setSelectedMatches((prev) => ({ ...prev, ...selected }));
      }
    }
  }, [data]);

  const handleTimeRangeChange = (event) => {
    const value = event.target.value;
    let durationType = "";
    if (value === "daily") {
      durationType = "daily";
    } else if (value === "week") {
      durationType = "week";
    } else if (value === "monthly") {
      durationType = "monthly";
    } else if (value === "season") {
      durationType = "season";
    }
    setSelectedMatches({
      daily: [],
      weekly: {},
      monthly: {},
      season: {},
    });
    setAvailableMatches([]);
    setActiveWeekTab(1);
    setCurrentWeekPage(1);
    formik.setValues({
      ...formik.values,
      timeRange: value,
      durationType,
      startTime: "",
      endTime: "",
    });
    if (value === "daily" && formik.values.leagueId) {
    }
  };

  const callCountMatchTimeWise = async () => {
    try {
      if (!formik.values.startTime || !formik.values.endTime) {
        return;
      }
      const startDate = new Date(formik.values.startTime);
      const endDate = new Date(formik.values.endTime);
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return;
      }
      const payload = {
        leagueId: formik.values.leagueId,
        startTime: formik.values.startTime,
        endTime: formik.values.endTime,
      };
      const response = await contestServices.countMatchTimeWise(payload);
      if (response?.data?.data?.matchCount) {
        setMatchCount(response.data.data.matchCount);
      }
    } catch (error) {
      console.error("Error fetching match count:", error);
      setMatchCount(0);
    }
  };

  const getRemainingPercentage = () => {
    if (formik?.values?.gameType !== "playForFun") {
      const total = formik?.values?.payoutStructure.reduce((sum, item) => {
        const percent = parseFloat(item.percentage);
        return sum + (isNaN(percent) ? 0 : percent);
      }, 0);
      const remaining = 100 - total;
      return isNaN(remaining) ? 0 : remaining;
    }
    return 0;
  };

  const validateAndClearDateErrors = () => {
    if (
      formik.values.startTime &&
      formik.values.endTime &&
      formik.values.timeRange
    ) {
      const start = new Date(formik.values.startTime);
      const end = new Date(formik.values.endTime);
      if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
        let isValid = false;
        if (formik.values.timeRange === "daily") {
          const startDate = start.toDateString();
          const endDate = end.toDateString();
          isValid = startDate === endDate;
        } else if (formik.values.timeRange === "week") {
          // For weekly contests, calculate days inclusively
          const startDate = new Date(start);
          const endDate = new Date(end);
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(0, 0, 0, 0);
          const diffDays =
            Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
          isValid = diffDays === 7;
        } else if (formik.values.timeRange === "monthly") {
          const diffDays =
            Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
          isValid = diffDays >= 28 && diffDays <= 31;
        } else if (formik.values.timeRange === "season") {
          const diffDays =
            Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
          isValid = diffDays >= 28;
        }
        if (isValid) {
          if (formik.errors.startTime) {
            formik.setFieldError("startTime", "");
          }
          if (formik.errors.endTime) {
            formik.setFieldError("endTime", "");
          }
        }
      }
    }
  };

  const [matches, setMatches] = useState([]);

  const fetchMatchesByDate = useCallback(
    async (timeRange, startDate, endDate, leagueID) => {
      if (!startDate || !endDate || !leagueID) {
        return [];
      }
      const formatTimeToUTC = (dateString, isStartTime = true) => {
        if (!dateString) return null;
        const parsedDate = new Date(dateString);
        if (isNaN(parsedDate.getTime())) {
          return null;
        }
        const utcDate = new Date(parsedDate);
        if (isStartTime) {
          utcDate.setHours(0, 0, 0, 0);
        } else {
          utcDate.setHours(23, 59, 59, 999);
        }
        return utcDate.toISOString();
      };
      const payload = {
        leagueId: String(leagueID),
        startDate: formatTimeToUTC(startDate, true),
        endDate: formatTimeToUTC(endDate, false),
      };
      try {
        const response = await getLeagueRoundsWithMatches.getLeagueRounds(
          payload
        );
        const fetchedRounds = response.data.data.rounds || [];
        setRounds(fetchedRounds);
        const allMatches = fetchedRounds.flatMap(
          (round) => round.matches || []
        );
        setMatches(allMatches);
        return fetchedRounds;
      } catch (err) {
        console.error("Error fetching matches", err);
        return [];
      }
    },
    []
  );

  const handleMatchSelection = (_id, timeRange, weekKey = null) => {
    const newState = { ...selectedMatches };
    if (timeRange === "daily") {
      if (newState.daily.includes(_id)) {
        newState.daily = newState.daily.filter((id) => id !== _id);
      } else {
        newState.daily.push(_id);
      }
    } else {
      let stateKey;
      if (timeRange === "week") stateKey = "weekly";
      else if (timeRange === "monthly") stateKey = "monthly";
      else if (timeRange === "season") stateKey = "season";
      else stateKey = timeRange;
      if (!newState[stateKey]) {
        newState[stateKey] = {};
      }
      if (!newState[stateKey][weekKey]) {
        newState[stateKey][weekKey] = [];
      }
      if (newState[stateKey][weekKey].includes(_id)) {
        newState[stateKey][weekKey] = newState[stateKey][weekKey].filter(
          (id) => id !== _id
        );
      } else {
        newState[stateKey][weekKey].push(_id);
      }
    }
    setSelectedMatches(newState);
    // Check if we need to reset contest type for daily contests with less than 3 matches
    if (timeRange === "daily" && formik.values.timeRange === "daily") {
      const newDailyCount = newState.daily.length;
      const currentContestType = formik.values.contestType?.type;
      // If we have less than 3 matches and current contest type is not position/action, reset it
      if (
        newDailyCount < 3 &&
        currentContestType &&
        currentContestType !== "position" &&
        currentContestType !== "action"
      ) {
        formik.setFieldValue("contestType.type", "");
        toast.warning(
          "Contest type has been reset. With less than 3 matches, only Position or Action types are allowed."
        );
      }
    }
  };

  const handleSelectAllMatchesInWeek = (timeRange, weekKey) => {
    const newState = { ...selectedMatches };
    let stateKey;
    if (timeRange === "week") stateKey = "weekly";
    else if (timeRange === "monthly") stateKey = "monthly";
    else if (timeRange === "season") stateKey = "season";
    else return;
    if (!newState[stateKey]) {
      newState[stateKey] = {};
    }
    const round = availableMatches.find((r) => r.uiKey === weekKey);
    const weekMatches = round ? round.matches : [];
    newState[stateKey][weekKey] = weekMatches.map((match) => match._id);
    setSelectedMatches(newState);
  };

  const handleDeselectAllMatchesInWeek = (timeRange, weekKey) => {
    const newState = { ...selectedMatches };
    let stateKey;
    if (timeRange === "week") stateKey = "weekly";
    else if (timeRange === "monthly") stateKey = "monthly";
    else if (timeRange === "season") stateKey = "season";
    else return;
    if (newState[stateKey] && newState[stateKey][weekKey]) {
      newState[stateKey][weekKey] = [];
      setSelectedMatches(newState);
    }
  };

  const handleSelectAllMatchesInTimeRange = (timeRange) => {
    const newState = { ...selectedMatches };
    let stateKey;
    if (timeRange === "week") stateKey = "weekly";
    else if (timeRange === "monthly") stateKey = "monthly";
    else if (timeRange === "season") stateKey = "season";
    else return;
    if (!newState[stateKey]) {
      newState[stateKey] = {};
    }
    availableMatches.forEach((round) => {
      const weekKey = round.uiKey;
      const weekMatches = round.matches || [];
      newState[stateKey][weekKey] = weekMatches.map((match) => match._id);
    });
    setSelectedMatches(newState);
  };

  const handleDeselectAllMatchesInTimeRange = (timeRange) => {
    const newState = { ...selectedMatches };
    let stateKey;
    if (timeRange === "week") stateKey = "weekly";
    else if (timeRange === "monthly") stateKey = "monthly";
    else if (timeRange === "season") stateKey = "season";
    else return;
    if (newState[stateKey]) {
      newState[stateKey] = {};
      setSelectedMatches(newState);
    }
  };

  const isMatchSelected = (_id, timeRange, weekKey = null) => {
    if (timeRange === "daily") {
      return selectedMatches.daily.includes(_id);
    } else if (weekKey) {
      let stateKey;
      if (timeRange === "week") stateKey = "weekly";
      else if (timeRange === "monthly") stateKey = "monthly";
      else if (timeRange === "season") stateKey = "season";
      else stateKey = timeRange;
      const isSelected =
        selectedMatches[stateKey]?.[weekKey]?.includes(_id) || false;
      return isSelected;
    }
    return false;
  };

  const getCurrentWeekPage = (weekKeys) => {
    const totalPages = Math.ceil(weekKeys.length / weeksPerPage);
    const startIndex = (currentWeekPage - 1) * weeksPerPage;
    const endIndex = startIndex + weeksPerPage;
    return weekKeys.slice(startIndex, endIndex);
  };

  const handleWeekPageChange = (newPage) => {
    setCurrentWeekPage(newPage);
    setActiveWeekTab(1);
  };

  const handleQuickWeekSelect = (weekKey) => {
    const weekKeys = availableMatches.map((r) => r.uiKey);
    const weekIndex = weekKeys.indexOf(weekKey);
    const pageNumber = Math.floor(weekIndex / weeksPerPage) + 1;
    setCurrentWeekPage(pageNumber);
    const globalWeekNumber = weekIndex + 1;
    setActiveWeekTab(globalWeekNumber);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    formik.validateForm().then((errors) => {
      if (Object.keys(errors).length > 0) {
        toast.error("All fields are required");
        formik.setErrors(errors);
        const setTouchedFromErrors = (errs, prefix = "") => {
          const touched = {};
          Object.keys(errs || {}).forEach((key) => {
            const path = prefix ? `${prefix}.${key}` : key;
            const val = errs[key];
            if (typeof val === "string") {
              touched[key] = true;
            } else if (Array.isArray(val)) {
              touched[key] = val.map((item, i) =>
                typeof item === "object" && item !== null
                  ? setTouchedFromErrors(item, `${path}[${i}]`)
                  : true
              );
            } else if (val && typeof val === "object") {
              touched[key] = setTouchedFromErrors(val, path);
            }
          });
          return touched;
        };
        formik.setTouched(setTouchedFromErrors(errors));
      } else {
        formik.handleSubmit(e);
      }
    });
  };

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: ContestSchema,
    validateOnBlur: true,
    validateOnChange: true,
    context: validationContext,
    initialValues: {
      contestName: data?.contestName || "",
      leagueId: data?.leagueId?._id || "",
      country: data?.countryId || "",
      type: data?.type || "",
      contestType: {
        type: data?.contestType?.type || "",
        playerPosition: data?.contestType?.playerPosition || "",
        // negativeType: data?.contestType?.negativeType || '',
        playerActionType: data?.contestType?.actionType || "",
      },
      gameType: data?.gameType || "",
      numberOfWinners: data?.numberOfWinners || "",
      payoutStructure: data?.payoutStructure || [
        { startPosition: "", endPosition: "", percentage: "" },
      ],
      entryFee: data?.entryFee || "",
      contestCommission: data?.contestCommission,
      timeRange: data?.gameDuration?.type || "",
      startTime: data?.gameDuration?.startTime || "",
      endTime: data?.gameDuration?.endTime || "",
      durationType: data?.gameDuration?.type || "",
      totalRoomSize: data?.roomSize?.totalRoomSize || "",
      roomType: data?.roomSize?.type || "",
    },
    onSubmit: async (values) => {
      if (formik.values.gameType !== "playForFun") {
        const remainingPercentage = getRemainingPercentage();
        if (remainingPercentage !== 0) {
          toast.error(
            `Total payout percentage must equal 100%. Remaining percentage: ${remainingPercentage}%`
          );
          return;
        }
      }
      if (
        !formik.values.timeRange ||
        !formik.values.startTime ||
        !formik.values.endTime
      ) {
        toast.error("Please select time range and dates");
        return;
      }
      if (formik.values.timeRange === "week") {
        const start = new Date(formik.values.startTime);
        const end = new Date(formik.values.endTime);
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);
        const diffDays = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
        if (diffDays !== 7) {
          toast.error(
            `Weekly contests must be exactly 7 days. Current selection: ${diffDays} days`
          );
          return;
        }
      }
      if (formik.values.timeRange === "monthly") {
        const start = new Date(formik.values.startTime);
        const end = new Date(formik.values.endTime);
        const diffDays = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
        if (diffDays < 28 || diffDays > 31) {
          toast.error(
            `Monthly contests must be between 28 and 31 days. Current selection: ${diffDays} days`
          );
          return;
        }
      }
      let hasSelectedMatches = false;
      let selectedMatchesCount = 0;
      if (formik.values.timeRange === "daily") {
        hasSelectedMatches = selectedMatches.daily.length > 0;
        selectedMatchesCount = selectedMatches.daily.length;
      } else if (formik.values.timeRange === "week") {
        hasSelectedMatches = Object.values(selectedMatches.weekly).some(
          (weekMatches) => weekMatches.length > 0
        );
        selectedMatchesCount = Object.values(selectedMatches.weekly).reduce(
          (total, weekMatches) => total + weekMatches.length,
          0
        );
      } else if (formik.values.timeRange === "monthly") {
        hasSelectedMatches = Object.values(selectedMatches.monthly).some(
          (weekMatches) => weekMatches.length > 0
        );
        selectedMatchesCount = Object.values(selectedMatches.monthly).reduce(
          (total, weekMatches) => total + weekMatches.length,
          0
        );
      } else if (formik.values.timeRange === "season") {
        hasSelectedMatches = Object.values(selectedMatches.season).some(
          (weekMatches) => weekMatches.length > 0
        );
        selectedMatchesCount = Object.values(selectedMatches.season).reduce(
          (total, weekMatches) => total + weekMatches.length,
          0
        );
      }
      if (!hasSelectedMatches) {
        toast.error("Please select at least one match");
        return;
      }
      // Validation for game type specific match requirements
      const contestType = formik.values.contestType?.type;
      if (formik.values.timeRange === "daily") {
        // For daily contests, check match count requirements
        if (selectedMatchesCount >= 3) {
          // With 3+ matches, all game types are allowed
          if (selectedMatchesCount < 1) {
            toast.error("Please select at least 1 match");
            return;
          }
        } else {
          // With less than 3 matches, only position/action allowed
          if (contestType !== "position" && contestType !== "action") {
            toast.error(
              "With less than 3 matches, only Position or Action contest types are allowed"
            );
            return;
          }
          if (selectedMatchesCount < 1) {
            toast.error(
              "Please select at least 1 match for position/action contests"
            );
            return;
          }
        }
      } else {
        // For non-daily contests, use the original logic
        if (contestType === "position" || contestType === "action") {
          // For position or action game types, allow 1 match
          if (selectedMatchesCount < 1) {
            toast.error(
              "Please select at least 1 match for position/action contests"
            );
            return;
          }
        } else {
          // For other game types, require minimum 2 matches
          if (selectedMatchesCount < 3) {
            toast.error(
              "Please select at least 3 matches for this game type (minimum 6 teams required)"
            );
            return;
          }
        }
      }
      // setLoading(true);
      try {
        const formatTimeToUTC = (dateString, isStartTime = true) => {
          if (!dateString) return null;
          const date = new Date(dateString);
          if (isNaN(date.getTime())) {
            console.error(
              "Invalid date passed to formatTimeToUTC:",
              dateString
            );
            return null;
          }
          if (isStartTime) {
            date.setHours(0, 0, 1, 0);
          } else {
            date.setHours(23, 59, 59, 999);
          }
          return date.toISOString();
        };
        let matchesPayload = {};
        if (formik.values.timeRange === "daily") {
          const weekPayload = {
            matches: selectedMatches.daily,
            startDate: formatTimeToUTC(formik.values.startTime, true),
            endDate: formatTimeToUTC(formik.values.endTime, false),
          };
          // Add roundId from the first round if available
          if (rounds && rounds.length > 0) {
            weekPayload.roundId = rounds[0].roundId || rounds[0]._id;
          }
          if (id && gameWeekIdMap["week1"]) {
            weekPayload.gameWeekId = gameWeekIdMap["week1"];
          }
          matchesPayload = {
            gameweek: {
              week1: weekPayload,
            },
          };
        } else if (
          ["week", "monthly", "season"].includes(formik.values.timeRange)
        ) {
          let stateKey;
          if (formik.values.timeRange === "week") stateKey = "weekly";
          else if (formik.values.timeRange === "monthly") stateKey = "monthly";
          else if (formik.values.timeRange === "season") stateKey = "season";
          else stateKey = formik.values.timeRange;
          const roundsPayload = availableMatches.reduce((acc, round, index) => {
            const weekKey = `week${index + 1}`;
            const selectedMatchesForRound =
              selectedMatches[stateKey]?.[weekKey] || [];
            const roundPayload = {
              matches: selectedMatchesForRound,
              startDate: formatTimeToUTC(round.startDate.toISOString(), true),
              endDate: formatTimeToUTC(round.endDate.toISOString(), false),
              roundId: round.roundId || round._id,
            };
            if (id && gameWeekIdMap[weekKey]) {
              roundPayload.gameWeekId = gameWeekIdMap[weekKey];
            }
            acc[weekKey] = roundPayload;
            return acc;
          }, {});
          matchesPayload = {
            gameweek: roundsPayload,
          };
        }
        const contestName = values.contestName ? values.contestName.trim() : "";
        const position = values.contestType?.playerPosition || "";
        const action =
          values.contestType?.actionType ||
          values.contestType?.playerActionType ||
          "";
        let actionEventId = null;
        if (position && positionActionsMap && positionActionsMap[position]) {
          const found = positionActionsMap[position].find(
            (a) => a.value === action
          );
          actionEventId = found ? found.actionEventId : null;
        }
        if (action && actions) {
          const found = actions.find((a) => a.value === action);
          actionEventId = found ? found.actionEventId : null;
        }
        const payload = {
          id: id || undefined,
          contestName,
          leagueId: values.leagueId,
          type: values.type,
          contestType: {
            type: values.contestType.type,
            ...(values.contestType.type === "position" && {
              playerPosition: values.contestType.playerPosition,
            }),
            // ...(values.contestType.type === "negative-point" && { negativeType: values.contestType.negativeType }),
            ...(values.contestType.type === "action" && {
              playerPosition: position,
              actionType: values.contestType.playerActionType,
              actionEventId,
            }),
          },
          gameType: values.gameType,
          roomType: values.roomType,
          ...(values.roomType === "limited" && {
            totalRoomSize: values.totalRoomSize,
          }),
          numberOfWinners: values.numberOfWinners,
          ...(values.gameType !== "playForFun" && {
            payoutStructure: values.payoutStructure,
          }),
          timeRange: values.timeRange,
          startTime: formatTimeToUTC(values.startTime, true),
          endTime: formatTimeToUTC(values.endTime, false),
          durationType: values.durationType,
          countryId: values.country,
          ...(values.gameType !== "playForFun" && {
            entryFee: values.entryFee,
          }),
          contestCommission: values.contestCommission,
          selectedMatches: matchesPayload,
        };

        const response = id
          ? await contestServices.edit(payload)
          : await contestServices.add(payload);
        toast.success(response?.data?.message, { toastId: "success" });
        // Navigate back to contest management with preserved page and view mode
        const page = searchParams.get("page");
        const view = searchParams.get("view");
        if (page && view) {
          navigate(`/contest-management?page=${page}&view=${view}`);
        } else if (page) {
          navigate(`/contest-management?page=${page}`);
        } else if (view) {
          navigate(`/contest-management?view=${view}`);
        } else {
          navigate(-1);
        }
      } catch (error) {
        errorHandler(error || error.response?.data?.message);
        // toast.error("Error submitting contest. Check console.");
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (formik.values.type === "head-to-head") {
      formik.setValues((prevValues) => ({
        ...prevValues,
        roomType: "limited",
        totalRoomSize: "2",
        numberOfWinners: "1",
        payoutStructure: [
          { startPosition: "1", endPosition: "1", percentage: "100" },
        ],
      }));
    }
  }, [formik.values.type]);

  useEffect(() => {
    setValidationContext({
      gameType: formik.values.gameType,
      numberOfWinners: formik.values.numberOfWinners,
    });
  }, [formik.values.gameType, formik.values.numberOfWinners]);

  const fetchLeagues = useCallback(async () => {
    try {
      const payload = {
        countryId: [formik.values.country] || [],
      };
      const response = await contestServices?.getAllLeagues(payload);
      setLeagues(response?.data?.data?.leagues || []);
    } catch (error) {
      errorHandler(error);
    }
  }, [formik.values.country]);

  useEffect(() => {
    fetchContestData();
    fetchLeagues();
  }, [fetchContestData, fetchLeagues, id]);

  useEffect(() => {
    const fatchAllGames = async () => {
      try {
        const payload = {
          searchItem: "",
          status: 1,
          pageNumber: 1,
          pageSize: 100,
        };
        const response = await gameManagementApi.getAllGames(payload);
        const result = response?.data?.data?.result;
        const positionGame = result.find((item) => item?.type === "position");
        const positions = positionGame?.rules?.[0]?.allowedPositions || [];
        const negativePoint = result.find(
          (item) => item?.type === "negative-point"
        );
        const NegativeValue = negativePoint?.rules?.[0]?.allowedPoints || [];
        const action = result.find((item) => item?.type === "action");
        const actionValue = action?.rules?.[0]?.allowedPositions || [];
        const formattedData = {};
        actionValue.forEach((position) => {
          formattedData[position.value] = position.allowedAction.map(
            (action) => ({
              value: action.value,
              label: action.title,
              actionEventId: action.actionEventId,
            })
          );
        });
        const actionRules = result.find((item) => item?.type === "action");
        const actionAllowedPoints =
          actionRules?.rules?.[0]?.allowedPoints || [];
        setActions(actionAllowedPoints);
        setPositionActionsMap(formattedData);
      } catch (error) {}
    };
    fatchAllGames();
  }, []);

  useEffect(() => {
    if (isFetchingMatches) {
      return;
    }
    const fetchAndSetMatches = async () => {
      if (
        formik.values.timeRange &&
        formik.values.startTime &&
        formik.values.endTime &&
        formik.values.leagueId &&
        formik.values.startTime !== "" &&
        formik.values.endTime !== ""
      ) {
        const currentFetch = {
          timeRange: formik.values.timeRange,
          startTime: formik.values.startTime,
          endTime: formik.values.endTime,
          leagueId: formik.values.leagueId,
        };
        if (
          lastFetchRef.current.timeRange === currentFetch.timeRange &&
          lastFetchRef.current.startTime === currentFetch.startTime &&
          lastFetchRef.current.endTime === currentFetch.endTime &&
          lastFetchRef.current.leagueId === currentFetch.leagueId
        ) {
          return;
        }
        lastFetchRef.current = currentFetch;
        setIsFetchingMatches(true);
        try {
          const startDate = new Date(formik.values.startTime);
          const endDate = new Date(formik.values.endTime);
          if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
            return;
          }
          const fetchedRounds = await fetchMatchesByDate(
            formik.values.timeRange,
            formik.values.startTime,
            formik.values.endTime,
            formik.values.leagueId
          );
          if (formik.values.timeRange === "daily") {
            const allMatches = fetchedRounds.flatMap((round) =>
              (round.matches || []).map((match) => ({
                ...match,
                _id: match.matchId || match._id,
                status:
                  match.state === "Not Started"
                    ? 1
                    : match.state === "Live"
                    ? 2
                    : 3,
                liveStats: match.state === "Live" ? { status: "live" } : null,
              }))
            );
            setAvailableMatches(allMatches);
            if (!id) {
              const dailyMatchIds = allMatches.map((match) => match._id);
              setSelectedMatches((prev) => ({
                ...prev,
                daily: dailyMatchIds,
              }));
            }
          } else {
            const processedRounds = fetchedRounds
              .map((round, index) => ({
                ...round,
                uiKey: `week${index + 1}`,
                roundNumber: index + 1,
                startDate: new Date(round.startDate),
                endDate: new Date(round.endDate),
                matches: (round.matches || []).map((match) => ({
                  ...match,
                  _id: match.matchId || match._id,
                  status:
                    match.state === "Not Started"
                      ? 1
                      : match.state === "Live"
                      ? 2
                      : 3,
                  liveStats: match.state === "Live" ? { status: "live" } : null,
                })),
              }))
              .filter((r) => r.matches.length > 0);
            setAvailableMatches(processedRounds);
            if (!id) {
              let stateKey;
              if (formik.values.timeRange === "week") stateKey = "weekly";
              else if (formik.values.timeRange === "monthly")
                stateKey = "monthly";
              else if (formik.values.timeRange === "season")
                stateKey = "season";
              const selectedByRound = {};
              processedRounds.forEach((round) => {
                selectedByRound[round.uiKey] = round.matches.map((m) => m._id);
              });
              setSelectedMatches((prev) => ({
                ...prev,
                [stateKey]: selectedByRound,
              }));
            }
          }
          setActiveWeekTab(1);
        } catch (error) {
          console.error("Error in fetchAndSetMatches:", error);
        } finally {
          setIsFetchingMatches(false);
        }
      }
    };
    if (
      formik.values.timeRange &&
      formik.values.startTime &&
      formik.values.endTime &&
      formik.values.leagueId
    ) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        fetchAndSetMatches();
      }, 300);
    }
    return () => {
      setIsFetchingMatches(false);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [
    formik.values.timeRange,
    formik.values.startTime,
    formik.values.endTime,
    formik.values.leagueId,
    isFetchingMatches,
    id,
    selectedMatches,
  ]);

  useEffect(() => {
    validateAndClearDateErrors();
  }, [formik.values.startTime, formik.values.endTime, formik.values.timeRange]);

  return (
    <Box>
      <CardLayout>
        <CardHeader
          title={
            <Box display="flex" alignItems="center" gap={2}>
              <SportsSoccer sx={{ color: "primary.main", fontSize: 28 }} />
              <Typography variant="h4" component="h1">
                {id ? "Edit" : "Create"} Contest
              </Typography>
            </Box>
          }
          subheader="Configure contest settings and rules for your fantasy sports competition"
        />
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleFormSubmit}>
              <Stack spacing={4}>
                {/* Basic Contest Information */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2} mb={3}>
                    <Business sx={{ color: "primary.main" }} />
                    <Typography variant="h6" fontWeight="600">
                      Basic Information
                    </Typography>
                  </Box>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Contest Name"
                        name="contestName"
                        value={formik.values.contestName}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.contestName &&
                          Boolean(formik.errors.contestName)
                        }
                        helperText={
                          formik.touched.contestName &&
                          formik.errors.contestName
                        }
                        placeholder="Enter contest name"
                        size="medium"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        select
                        fullWidth
                        label="Select Country"
                        name="country"
                        value={formik.values.country}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.country &&
                          Boolean(formik.errors.country)
                        }
                        helperText={
                          formik.touched.country && formik.errors.country
                        }
                        disabled={id ? true : false}
                        size="medium"
                      >
                        {country?.map((league) => (
                          <MenuItem
                            key={league?.countryId}
                            value={league?.countryId}
                          >
                            {league?.country}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        select
                        fullWidth
                        label="League"
                        name="leagueId"
                        value={leagues.length > 0 ? formik.values.leagueId : ""}
                        onChange={(e) => {
                          formik.setFieldValue("leagueId", e.target.value);
                          if (
                            formik.values.timeRange === "daily" &&
                            e.target.value &&
                            formik.values.startTime &&
                            formik.values.endTime
                          ) {
                            setTimeout(() => callCountMatchTimeWise(), 100);
                          }
                        }}
                        error={
                          formik.touched.leagueId &&
                          Boolean(formik.errors.leagueId)
                        }
                        helperText={
                          formik.touched.leagueId && formik.errors.leagueId
                        }
                        disabled={id ? true : false}
                        size="medium"
                      >
                        {leagues?.map((league) => (
                          <MenuItem key={league._id} value={league._id}>
                            {league.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                </Paper>
                {/* Contest Configuration */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2} mb={3}>
                    <Settings sx={{ color: "primary.main" }} />
                    <Typography variant="h6" fontWeight="600">
                      Contest Configuration
                    </Typography>
                  </Box>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        select
                        fullWidth
                        label="Type"
                        name="type"
                        value={formik.values.type}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.type && Boolean(formik.errors.type)
                        }
                        helperText={formik.touched.type && formik.errors.type}
                        size="medium"
                      >
                        <MenuItem value="public">Public</MenuItem>
                        {/* <MenuItem value="private">Private</MenuItem> */}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        select
                        fullWidth
                        label="Contest Type"
                        name="gameType"
                        value={formik.values.gameType}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.gameType &&
                          Boolean(formik.errors.gameType)
                        }
                        helperText={
                          formik.touched.gameType && formik.errors.gameType
                        }
                        size="medium"
                      >
                        <MenuItem value="playForMoney">Play For Money</MenuItem>
                        <MenuItem value="playForFun">Play For Fun</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        select
                        fullWidth
                        label="Room Type"
                        name="roomType"
                        disabled={formik.values.type == "head-to-head"}
                        value={
                          formik.values.type == "head-to-head"
                            ? "limited"
                            : formik.values.roomType
                        }
                        onChange={(e) => {
                          const value = e.target.value;
                          formik.setValues({
                            ...formik.values,
                            roomType: value,
                            totalRoomSize:
                              value === "limited"
                                ? formik.values.totalRoomSize
                                : formik.values.totalRoomSize,
                          });
                        }}
                        error={
                          formik.touched.roomType &&
                          Boolean(formik.errors.roomType)
                        }
                        helperText={
                          formik.touched.roomType && formik.errors.roomType
                        }
                        size="medium"
                      >
                        <MenuItem value="limited">Limited</MenuItem>
                        <MenuItem value="unlimited">Unlimited</MenuItem>
                      </TextField>
                    </Grid>
                    {formik.values.roomType === "limited" && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Room Size"
                          name="totalRoomSize"
                          disabled={formik.values.type == "head-to-head"}
                          value={
                            formik.values.type == "head-to-head"
                              ? 2
                              : formik.values.totalRoomSize
                          }
                          onChange={(e) => {
                            const value = e.target.value
                              .replace(/[^0-9]/g, "")
                              .slice(0, 9);
                            formik.setFieldValue("totalRoomSize", value);
                          }}
                          error={
                            formik.touched.totalRoomSize &&
                            Boolean(formik.errors.totalRoomSize)
                          }
                          helperText={
                            formik.touched.totalRoomSize &&
                            formik.errors.totalRoomSize
                          }
                          placeholder="Enter room size"
                          size="medium"
                        />
                      </Grid>
                    )}
                    {formik.values.gameType === "playForFun" ? null : (
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="No. of Winners"
                          name="numberOfWinners"
                          disabled={formik.values.type == "head-to-head"}
                          value={
                            formik.values.type == "head-to-head"
                              ? 1
                              : formik.values.numberOfWinners
                          }
                          onChange={(e) => {
                            const value = e.target.value
                              .replace(/[^0-9]/g, "")
                              .slice(0, 9);
                            formik.setFieldValue("numberOfWinners", value);
                          }}
                          error={
                            formik.touched.numberOfWinners &&
                            Boolean(formik.errors.numberOfWinners)
                          }
                          helperText={
                            formik.touched.numberOfWinners &&
                            formik.errors.numberOfWinners
                          }
                          placeholder="Enter number of winners"
                          size="medium"
                        />
                      </Grid>
                    )}
                  </Grid>
                </Paper>
                {/* Financial Configuration */}
                {formik.values.gameType !== "playForFun" && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2} mb={3}>
                      <AttachMoney sx={{ color: "primary.main" }} />
                      <Typography variant="h6" fontWeight="600">
                        Financial Configuration
                      </Typography>
                    </Box>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Entry Fees"
                          name="entryFee"
                          value={formik.values.entryFee}
                          onChange={(e) => {
                            let value = e.target.value;
                            value = value.replace(/[^0-9.]/g, "");
                            if (value.indexOf(".") !== -1) {
                              const parts = value.split(".");
                              if (parts[1]?.length > 2) {
                                value = `${parts[0]}.${parts[1].slice(0, 2)}`;
                              }
                            }
                            if (value.replace(".", "").length > 7) {
                              value = value.slice(
                                0,
                                value.indexOf(".") !== -1
                                  ? value.indexOf(".") + 3
                                  : 7
                              );
                            }
                            formik.setFieldValue("entryFee", value);
                          }}
                          error={
                            formik.touched.entryFee &&
                            Boolean(formik.errors.entryFee)
                          }
                          helperText={
                            formik.touched.entryFee && formik.errors.entryFee
                          }
                          placeholder="0.00"
                          size="medium"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Entry Commission"
                          name="contestCommission"
                          value={formik.values.contestCommission}
                          InputLabelProps={{ shrink: true }}
                          onChange={(e) => {
                            let value = e.target.value;
                            value = value.replace(/[^0-9.]/g, "");
                            if (value.indexOf(".") !== -1) {
                              const parts = value.split(".");
                              if (parts[1]?.length > 2) {
                                value = `${parts[0]}.${parts[1].slice(0, 2)}`;
                              }
                            }
                            if (value.replace(".", "").length > 7) {
                              value = value.slice(
                                0,
                                value.indexOf(".") !== -1
                                  ? value.indexOf(".") + 3
                                  : 7
                              );
                            }
                            formik.setFieldValue("contestCommission", value);
                          }}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.contestCommission &&
                            Boolean(formik.errors.contestCommission)
                          }
                          helperText={
                            formik.touched.contestCommission &&
                            formik.errors.contestCommission
                          }
                          placeholder="0.00"
                          size="medium"
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                )}
                {/* Payout Structure */}
                {formik.values.gameType !== "playForFun" &&
                  formik.values.type != "head-to-head" && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={2} mb={3}>
                        <EmojiEvents sx={{ color: "primary.main" }} />
                        <Typography variant="h6" fontWeight="600">
                          Payout Structure
                        </Typography>
                      </Box>
                      <Box mb={3}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          Configure the winning structure and percentage
                          distribution for contest participants.
                        </Alert>
                      </Box>
                      <FieldArray
                        name="payoutStructure"
                        render={(arrayHelpers) => (
                          <Box>
                            {formik.values.payoutStructure.map(
                              (item, index) => (
                                <Card
                                  key={index}
                                  sx={{
                                    mb: 2,
                                    border: "1px solid",
                                    borderColor: "divider",
                                  }}
                                >
                                  <CardContentInner sx={{ p: 2 }}>
                                    <Grid
                                      container
                                      spacing={2}
                                      alignItems="center"
                                    >
                                      <Grid item xs={12} sm={3}>
                                        <TextField
                                          fullWidth
                                          label="Winner Start No."
                                          name={`payoutStructure.${index}.startPosition`}
                                          value={item.startPosition}
                                          onChange={(e) => {
                                            const value =
                                              e.target.value.replace(
                                                /[^0-9]/g,
                                                ""
                                              );
                                            formik.setFieldValue(
                                              `payoutStructure.${index}.startPosition`,
                                              value
                                            );
                                          }}
                                          error={
                                            formik.touched.payoutStructure?.[
                                              index
                                            ]?.startPosition &&
                                            Boolean(
                                              formik.errors.payoutStructure?.[
                                                index
                                              ]?.startPosition
                                            )
                                          }
                                          helperText={
                                            formik.touched.payoutStructure?.[
                                              index
                                            ]?.startPosition &&
                                            formik.errors.payoutStructure?.[
                                              index
                                            ]?.startPosition
                                          }
                                          size="small"
                                        />
                                      </Grid>
                                      <Grid item xs={12} sm={3}>
                                        <TextField
                                          fullWidth
                                          label="Winner End No."
                                          name={`payoutStructure.${index}.endPosition`}
                                          value={item.endPosition}
                                          onChange={(e) => {
                                            const value =
                                              e.target.value.replace(
                                                /[^0-9]/g,
                                                ""
                                              );
                                            formik.setFieldValue(
                                              `payoutStructure.${index}.endPosition`,
                                              value
                                            );
                                          }}
                                          error={
                                            formik.touched.payoutStructure?.[
                                              index
                                            ]?.endPosition &&
                                            Boolean(
                                              formik.errors.payoutStructure?.[
                                                index
                                              ]?.endPosition
                                            )
                                          }
                                          helperText={
                                            formik.touched.payoutStructure?.[
                                              index
                                            ]?.endPosition &&
                                            formik.errors.payoutStructure?.[
                                              index
                                            ]?.endPosition
                                          }
                                          size="small"
                                        />
                                      </Grid>
                                      <Grid item xs={12} sm={3}>
                                        <TextField
                                          fullWidth
                                          label="Winning %"
                                          name={`payoutStructure.${index}.percentage`}
                                          value={item.percentage}
                                          onChange={(e) => {
                                            const value = e.target.value;
                                            if (/^\d*\.?\d{0,2}$/.test(value)) {
                                              const updatedStructure = [
                                                ...formik.values
                                                  .payoutStructure,
                                              ];
                                              updatedStructure[
                                                index
                                              ].percentage = value;
                                              const total =
                                                updatedStructure.reduce(
                                                  (sum, item) =>
                                                    sum +
                                                    Number(
                                                      item.percentage || 0
                                                    ),
                                                  0
                                                );
                                              if (total > 100) {
                                                setPercentageErrorIndex(index);
                                              } else {
                                                setPercentageErrorIndex(null);
                                              }
                                              formik.setFieldValue(
                                                "payoutStructure",
                                                updatedStructure
                                              );
                                            }
                                          }}
                                          error={
                                            (formik.touched.payoutStructure?.[
                                              index
                                            ]?.percentage &&
                                              Boolean(
                                                formik.errors.payoutStructure?.[
                                                  index
                                                ]?.percentage
                                              )) ||
                                            percentageErrorIndex === index
                                          }
                                          helperText={
                                            formik.touched.payoutStructure?.[
                                              index
                                            ]?.percentage &&
                                            formik.errors.payoutStructure?.[
                                              index
                                            ]?.percentage
                                              ? formik.errors.payoutStructure[
                                                  index
                                                ].percentage
                                              : percentageErrorIndex === index
                                              ? "Total payout percentage cannot exceed 100%"
                                              : ""
                                          }
                                          size="small"
                                        />
                                      </Grid>
                                      <Grid
                                        item
                                        xs={12}
                                        sm={3}
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                      >
                                        {formik.values.payoutStructure.length >
                                          1 && (
                                          <IconButton
                                            onClick={() =>
                                              arrayHelpers.remove(index)
                                            }
                                            color="error"
                                            size="small"
                                            sx={{
                                              border: "1px solid",
                                              borderColor: "error.main",
                                              "&:hover": {
                                                backgroundColor: "error.light",
                                                color: "white",
                                              },
                                            }}
                                          >
                                            <RemoveCircle />
                                          </IconButton>
                                        )}
                                        <IconButton
                                          onClick={() =>
                                            arrayHelpers.push({
                                              startPosition: "",
                                              endPosition: "",
                                              percentage: "",
                                            })
                                          }
                                          color="primary"
                                          size="small"
                                          sx={{
                                            border: "1px solid",
                                            borderColor: "primary.main",
                                            "&:hover": {
                                              backgroundColor: "primary.main",
                                              color: "white",
                                            },
                                          }}
                                        >
                                          <AddCircle />
                                        </IconButton>
                                      </Grid>
                                    </Grid>
                                  </CardContentInner>
                                </Card>
                              )
                            )}
                          </Box>
                        )}
                      />
                      <Box mt={2}>
                        <Chip
                          label={`Total Percentage: ${
                            getRemainingPercentage() === 0
                              ? "100%"
                              : `${100 - getRemainingPercentage()}%`
                          }`}
                          color={
                            getRemainingPercentage() === 0
                              ? "success"
                              : "warning"
                          }
                          variant="outlined"
                          size="medium"
                        />
                        {getRemainingPercentage() !== 0 && (
                          <Typography
                            variant="body2"
                            color="warning.main"
                            mt={1}
                          >
                            Remaining: {getRemainingPercentage()}%
                          </Typography>
                        )}
                      </Box>
                      <Grid item xs={12}>
                        {formik.errors.payoutStructure &&
                          typeof formik.errors.payoutStructure === "string" && (
                            <FormHelperText error>
                              {formik.errors.payoutStructure}
                            </FormHelperText>
                          )}
                      </Grid>
                    </Paper>
                  )}
                {/* Time Configuration */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2} mb={3}>
                    <Schedule sx={{ color: "primary.main" }} />
                    <Typography variant="h6" fontWeight="600">
                      Time Configuration
                    </Typography>
                  </Box>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        select
                        fullWidth
                        label="Game Period"
                        name="timeRange"
                        value={formik.values.timeRange}
                        onChange={handleTimeRangeChange}
                        error={
                          formik.touched.timeRange &&
                          Boolean(formik.errors.timeRange)
                        }
                        helperText={
                          formik.touched.timeRange && formik.errors.timeRange
                        }
                        size="medium"
                      >
                        <MenuItem value="daily">Daily</MenuItem>
                        <MenuItem value="week">Weekly</MenuItem>
                        <MenuItem value="monthly">Monthly</MenuItem>
                        <MenuItem value="season">Season</MenuItem>
                      </TextField>
                      {formik.values.timeRange === "daily" && (
                        <FormHelperText sx={{ mt: 1, color: "info.main" }}>
                          ⓘ Daily contests use the same start and end date
                        </FormHelperText>
                      )}
                      {formik.values.timeRange === "week" && (
                        <FormHelperText sx={{ mt: 1, color: "info.main" }}>
                          ⓘ Weekly contests must span exactly 7 days from start
                          to end date
                        </FormHelperText>
                      )}
                      {formik.values.timeRange === "monthly" && (
                        <FormHelperText sx={{ mt: 1, color: "info.main" }}>
                          ⓘ Monthly contests must span between 28 and 31 days
                          from start to end date
                        </FormHelperText>
                      )}
                      {formik.values.timeRange === "season" && (
                        <FormHelperText sx={{ mt: 1, color: "info.main" }}>
                          ⓘ Season contests must span at least 1 month from
                          start to end date
                        </FormHelperText>
                      )}
                    </Grid>
                    {["season", "daily", "week", "monthly"].includes(
                      formik?.values?.timeRange
                    ) && (
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Grid
                          item
                          xs={formik?.values?.timeRange === "daily" ? 12 : 6}
                        >
                          <DatePicker
                            label="Start Date"
                            className="CustomCalenderbox"
                            format="dd/MM/yyyy"
                            minDate={new Date()}
                            value={
                              formik.values.startTime
                                ? new Date(formik.values.startTime)
                                : null
                            }
                            onChange={(date) => {
                              try {
                                if (
                                  date &&
                                  date instanceof Date &&
                                  !isNaN(date.getTime())
                                ) {
                                  const start = new Date(date);
                                  start.setHours(0, 0, 0, 0);
                                  const isoStart = start.toISOString();
                                  formik.setFieldValue("startTime", isoStart);
                                  // Validate and clear errors after updating start date
                                  setTimeout(
                                    () => validateAndClearDateErrors(),
                                    100
                                  );
                                  if (formik.values.timeRange === "daily") {
                                    const end = new Date(date);
                                    end.setHours(23, 59, 59, 999);
                                    const isoEnd = end.toISOString();
                                    formik.setFieldValue("endTime", isoEnd);
                                    if (formik.values.leagueId) {
                                      setTimeout(
                                        () => callCountMatchTimeWise(),
                                        100
                                      );
                                    }
                                  }
                                } else {
                                  formik.setFieldValue("startTime", "");
                                  if (formik.values.timeRange === "daily") {
                                    formik.setFieldValue("endTime", "");
                                  }
                                }
                              } catch (error) {
                                console.error(
                                  "Error handling start date change:",
                                  error
                                );
                                formik.setFieldValue("startTime", "");
                                if (formik.values.timeRange === "daily") {
                                  formik.setFieldValue("endTime", "");
                                }
                              }
                            }}
                            slots={{
                              textField: (params) => (
                                <TextField
                                  {...params}
                                  name="startTime"
                                  error={
                                    formik.touched.startTime &&
                                    Boolean(formik.errors.startTime)
                                  }
                                  helperText={formik.touched.startTime}
                                  fullWidth
                                  size="medium"
                                />
                              ),
                            }}
                          />
                          <FormHelperText
                            error={Boolean(formik.errors.startTime)}
                          >
                            {formik.errors.startTime}
                          </FormHelperText>
                        </Grid>
                        {formik?.values?.timeRange !== "daily" && (
                          <Grid item xs={6}>
                            <DatePicker
                              label="End Date"
                              className="CustomCalenderbox"
                              format="dd/MM/yyyy"
                              minDate={
                                formik.values.startTime
                                  ? new Date(formik.values.startTime)
                                  : new Date()
                              }
                              value={
                                formik.values.endTime
                                  ? new Date(formik.values.endTime)
                                  : null
                              }
                              error={
                                formik.touched.endTime &&
                                Boolean(formik.errors.endTime)
                              }
                              helperText={
                                formik.touched.endTime && formik.errors.endTime
                              }
                              onChange={(date) => {
                                try {
                                  if (
                                    date &&
                                    date instanceof Date &&
                                    !isNaN(date.getTime())
                                  ) {
                                    const end = new Date(date);
                                    end.setHours(23, 59, 59, 999);
                                    const isoEnd = end.toISOString();
                                    formik.setFieldValue("endTime", isoEnd);
                                    // Validate and clear errors after updating end date
                                    setTimeout(
                                      () => validateAndClearDateErrors(),
                                      100
                                    );
                                  } else {
                                    formik.setFieldValue("endTime", "");
                                  }
                                } catch (error) {
                                  console.error(
                                    "Error handling end date change:",
                                    error
                                  );
                                  formik.setFieldValue("endTime", "");
                                }
                              }}
                              slots={{
                                textField: (params) => (
                                  <TextField
                                    {...params}
                                    name="endTime"
                                    error={
                                      formik.touched.endTime &&
                                      Boolean(formik.errors.endTime)
                                    }
                                    required
                                    fullWidth
                                    size="medium"
                                  />
                                ),
                              }}
                            />
                            <FormHelperText
                              error={Boolean(formik.errors.endTime)}
                            >
                              {formik.errors.endTime}
                            </FormHelperText>
                            {/* Show day difference for weekly contests */}
                            {formik.values.timeRange === "week" &&
                              formik.values.startTime &&
                              formik.values.endTime && (
                                <FormHelperText
                                  sx={{ mt: 1, color: "info.main" }}
                                >
                                  {(() => {
                                    const start = new Date(
                                      formik.values.startTime
                                    );
                                    const end = new Date(formik.values.endTime);
                                    start.setHours(0, 0, 0, 0);
                                    end.setHours(0, 0, 0, 0);
                                    const diffDays =
                                      Math.floor(
                                        (end - start) / (1000 * 60 * 60 * 24)
                                      ) + 1;
                                    const isCorrect = diffDays === 7;
                                    return (
                                      <span
                                        style={{
                                          color: isCorrect
                                            ? "success.main"
                                            : "warning.main",
                                        }}
                                      >
                                        {isCorrect ? "✓" : "⚠"} {diffDays} days
                                        selected
                                        {!isCorrect &&
                                          " (Weekly contests must be exactly 7 days)"}
                                      </span>
                                    );
                                  })()}
                                </FormHelperText>
                              )}
                            {/* Show day difference for monthly contests */}
                            {formik.values.timeRange === "monthly" &&
                              formik.values.startTime &&
                              formik.values.endTime && (
                                <FormHelperText
                                  sx={{ mt: 1, color: "info.main" }}
                                >
                                  {(() => {
                                    const start = new Date(
                                      formik.values.startTime
                                    );
                                    const end = new Date(formik.values.endTime);
                                    const diffDays =
                                      Math.floor(
                                        (end - start) / (1000 * 60 * 60 * 24)
                                      ) + 1;
                                    const isCorrect =
                                      diffDays >= 28 && diffDays <= 31;
                                    return (
                                      <span
                                        style={{
                                          color: isCorrect
                                            ? "success.main"
                                            : "warning.main",
                                        }}
                                      >
                                        {isCorrect ? "✓" : "⚠"} {diffDays} days
                                        selected
                                        {!isCorrect &&
                                          diffDays < 28 &&
                                          " (Monthly contests must be at least 28 days)"}
                                        {!isCorrect &&
                                          diffDays > 31 &&
                                          " (Monthly contests cannot exceed 31 days)"}
                                      </span>
                                    );
                                  })()}
                                </FormHelperText>
                              )}
                          </Grid>
                        )}
                      </LocalizationProvider>
                    )}
                  </Grid>
                </Paper>
                {/* Match Selection Section */}
                {formik.values.timeRange &&
                  formik.values.startTime &&
                  formik.values.endTime && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={2} mb={3}>
                        <SportsSoccer sx={{ color: "primary.main" }} />
                        <Typography variant="h6" fontWeight="600">
                          Match Selection
                        </Typography>
                      </Box>
                      {/* Match Requirements Info */}
                      <Box
                        sx={{
                          p: 2,
                          mb: 3,
                          backgroundColor: "info.light",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "info.main",
                        }}
                      >
                        <Typography variant="body2" color="info.dark">
                          <strong>Match Requirements:</strong>{" "}
                          {(() => {
                            if (formik.values.timeRange === "daily") {
                              const selectedCount =
                                selectedMatches.daily.length;
                              if (selectedCount >= 3) {
                                return "With 3+ matches: All contest types are allowed";
                              } else {
                                return "With less than 3 matches: Only Position or Action contest types are allowed";
                              }
                            } else {
                              return formik.values.contestType?.type ===
                                "position" ||
                                formik.values.contestType?.type === "action"
                                ? "Position/Action contests require at least 1 match"
                                : "Other contest types require at least 2 matches (minimum 4 teams)";
                            }
                          })()}
                        </Typography>
                      </Box>
                      <Box className="match-selection-container">
                        <div className="match-content">
                          {/* Daily Matches Tab */}
                          {formik.values.timeRange === "daily" && (
                            <div className="tab-pane active daily-matches">
                              <div className="tab-header">
                                <div className="tab-header-top">
                                  <h6>
                                    Daily Matches -{" "}
                                    {formik.values.startTime
                                      ? new Date(
                                          formik.values.startTime
                                        ).toLocaleDateString("en-GB", {
                                          year: "numeric",
                                          month: "short",
                                          day: "numeric",
                                        })
                                      : ""}
                                  </h6>
                                  <span className="match-count">
                                    {availableMatches.length} matches available
                                    {/* (Debug: {availableMatches.length} total) */}
                                  </span>
                                </div>
                                <div className="tab-actions">
                                  <button
                                    type="button"
                                    className="tab-action-btn select-all-btn"
                                    onClick={() => {
                                      const allMatchIds = availableMatches.map(
                                        (match) => match._id
                                      );
                                      setSelectedMatches((prev) => ({
                                        ...prev,
                                        daily: allMatchIds,
                                      }));
                                    }}
                                    title="Select all daily matches"
                                  >
                                    Select All
                                  </button>
                                  <button
                                    type="button"
                                    className="tab-action-btn deselect-all-btn"
                                    onClick={() => {
                                      setSelectedMatches((prev) => ({
                                        ...prev,
                                        daily: [],
                                      }));
                                    }}
                                    title="Deselect all daily matches"
                                  >
                                    Deselect All
                                  </button>
                                </div>
                              </div>
                              <div className="match-list">
                                {availableMatches.length > 0 ? (
                                  availableMatches?.map((match) => {
                                    const homeTeam = match.participants?.find(
                                      (p) => p.meta?.location === "home"
                                    ) || {
                                      name:
                                        match.name?.split(" vs ")[0] ||
                                        "Team A",
                                    };
                                    const awayTeam = match.participants?.find(
                                      (p) => p.meta?.location === "away"
                                    ) || {
                                      name:
                                        match.name?.split(" vs ")[1] ||
                                        "Team B",
                                    };
                                    const matchDate = new Date(
                                      match.startingAt
                                    ).toLocaleDateString("en-GB", {
                                      weekday: "long",
                                      month: "short",
                                      day: "numeric",
                                    });
                                    const matchTime = new Date(
                                      match.startingAt
                                    ).toLocaleTimeString([], {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    });
                                    const statusText =
                                      match.status === 1
                                        ? "Scheduled"
                                        : match.liveStats?.status === "live"
                                        ? "Live"
                                        : "Completed";
                                    const statusClass =
                                      match.status === 1
                                        ? "status-1"
                                        : match.liveStats?.status === "live"
                                        ? "status-live"
                                        : "status-2";
                                    return (
                                      <div
                                        key={match._id}
                                        className={`match-item ${
                                          isMatchSelected(match._id, "daily")
                                            ? "selected"
                                            : ""
                                        }`}
                                        onClick={() =>
                                          handleMatchSelection(
                                            match._id,
                                            "daily"
                                          )
                                        }
                                      >
                                        <div className="match-teams">
                                          <img
                                            src={
                                              homeTeam?.imagePath ||
                                              "/images/default-team.png"
                                            }
                                            alt={homeTeam?.name}
                                          />
                                          <span>{homeTeam?.name}</span>
                                        </div>
                                        <div className="match-details">
                                          <div className="match-date">
                                            {matchDate}
                                          </div>
                                          <div className="match-time">
                                            {matchTime}
                                          </div>
                                        </div>
                                        <div className="match-teams">
                                          <span>{awayTeam?.name}</span>
                                          <img
                                            src={
                                              awayTeam?.imagePath ||
                                              "/images/default-team.png"
                                            }
                                            alt={awayTeam?.name}
                                          />
                                        </div>
                                      </div>
                                    );
                                  })
                                ) : (
                                  <div className="no-matches">
                                    <p>
                                      No matches available for the selected
                                      date.
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          {/* Weekly Matches Tab */}
                          {formik.values.timeRange === "week" && (
                            <div className="tab-pane active">
                              {(() => {
                                const weeks = availableMatches;
                                const weekKeys = weeks.map((w) => w.uiKey);
                                const totalMatchesInWeeks = weeks.reduce(
                                  (total, round) =>
                                    total + (round.matches?.length || 0),
                                  0
                                );
                                const currentWeeks =
                                  getCurrentWeekPage(weekKeys);
                                const totalPages = Math.ceil(
                                  weekKeys.length / weeksPerPage
                                );
                                return (
                                  <>
                                    <div className="tab-header">
                                      <div className="tab-header-top">
                                        <h6>Weekly Matches</h6>
                                        <span className="match-count">
                                          {weekKeys.length} rounds available
                                        </span>
                                      </div>
                                      <div className="tab-actions">
                                        <button
                                          type="button"
                                          className="tab-action-btn select-all-btn"
                                          onClick={() =>
                                            handleSelectAllMatchesInTimeRange(
                                              "week"
                                            )
                                          }
                                          title="Select all matches across all rounds"
                                        >
                                          Select All Rounds
                                        </button>
                                        <button
                                          type="button"
                                          className="tab-action-btn deselect-all-btn"
                                          onClick={() =>
                                            handleDeselectAllMatchesInTimeRange(
                                              "week"
                                            )
                                          }
                                          title="Deselect all matches across all rounds"
                                        >
                                          Deselect All Rounds
                                        </button>
                                      </div>
                                    </div>
                                    {weekKeys.length > 0 &&
                                    formik.values.startTime &&
                                    formik.values.endTime ? (
                                      <>
                                        <div className="week-tabs">
                                          <div className="week-tabs-header">
                                            <span className="week-tabs-title">
                                              Game Weeks
                                            </span>
                                            {totalPages > 1 && (
                                              <div className="week-pagination">
                                                <button
                                                  type="button"
                                                  className="week-pagination-btn"
                                                  onClick={() =>
                                                    handleWeekPageChange(
                                                      currentWeekPage - 1
                                                    )
                                                  }
                                                  disabled={
                                                    currentWeekPage === 1
                                                  }
                                                >
                                                  ‹
                                                </button>
                                                <span className="week-pagination-info">
                                                  Page {currentWeekPage} of{" "}
                                                  {totalPages}
                                                </span>
                                                <button
                                                  type="button"
                                                  className="week-pagination-btn"
                                                  onClick={() =>
                                                    handleWeekPageChange(
                                                      currentWeekPage + 1
                                                    )
                                                  }
                                                  disabled={
                                                    currentWeekPage ===
                                                    totalPages
                                                  }
                                                >
                                                  ›
                                                </button>
                                              </div>
                                            )}
                                          </div>
                                          <div className="week-tabs-grid">
                                            {currentWeeks.map((weekKey) => {
                                              const round = weeks.find(
                                                (w) => w.uiKey === weekKey
                                              );
                                              const actualRoundNumber =
                                                round.roundNumber;
                                              return (
                                                <button
                                                  type="button"
                                                  key={weekKey}
                                                  className={`week-tab ${
                                                    activeWeekTab ===
                                                    actualRoundNumber
                                                      ? "active"
                                                      : ""
                                                  } ${
                                                    (round.matches?.length ||
                                                      0) === 0
                                                      ? "empty-week"
                                                      : ""
                                                  }`}
                                                  onClick={() =>
                                                    setActiveWeekTab(
                                                      actualRoundNumber
                                                    )
                                                  }
                                                >
                                                  <span className="week-number">
                                                    Game Week{" "}
                                                    {round.roundName ||
                                                      actualRoundNumber}
                                                  </span>
                                                  <span className="week-dates">
                                                    {round.startDate.toLocaleDateString()}{" "}
                                                    -{" "}
                                                    {round.endDate.toLocaleDateString()}
                                                  </span>
                                                  {(round.matches?.length ||
                                                    0) === 0 && (
                                                    <span className="week-status">
                                                      No Matches
                                                    </span>
                                                  )}
                                                </button>
                                              );
                                            })}
                                          </div>
                                          {weekKeys.length > 12 && (
                                            <div className="week-quick-select">
                                              <span className="week-quick-select-label">
                                                Quick Jump:
                                              </span>
                                              <select
                                                className="week-quick-select-dropdown"
                                                onChange={(e) =>
                                                  handleQuickWeekSelect(
                                                    e.target.value
                                                  )
                                                }
                                                value=""
                                              >
                                                <option value="">
                                                  Select Round
                                                </option>
                                                {weeks.map((round, index) => {
                                                  const roundNumber = index + 1;
                                                  return (
                                                    <option
                                                      key={round.uiKey}
                                                      value={round.uiKey}
                                                    >
                                                      Round{" "}
                                                      {round.roundName ||
                                                        roundNumber}{" "}
                                                      (
                                                      {round.startDate.toLocaleDateString()}{" "}
                                                      -{" "}
                                                      {round.endDate.toLocaleDateString()}
                                                      )
                                                    </option>
                                                  );
                                                })}
                                              </select>
                                            </div>
                                          )}
                                        </div>
                                        {weeks.map((round, index) => {
                                          const actualRoundNumber = index + 1;
                                          const weekKey = round.uiKey;
                                          return (
                                            <div
                                              key={weekKey}
                                              className={`week-content ${
                                                weeks.length === 1 ||
                                                activeWeekTab ===
                                                  actualRoundNumber
                                                  ? "active"
                                                  : ""
                                              }`}
                                            >
                                              <div className="week-header">
                                                <div className="week-header-top">
                                                  <h6>
                                                    {weeks.length === 1
                                                      ? "Weekly Matches"
                                                      : `Game Week ${
                                                          round.roundName ||
                                                          actualRoundNumber
                                                        } Matches`}
                                                  </h6>
                                                  <span className="match-count">
                                                    {round.matches?.length || 0}{" "}
                                                    matches
                                                  </span>
                                                </div>
                                                <div className="week-actions">
                                                  <button
                                                    type="button"
                                                    className="week-action-btn select-all-btn"
                                                    onClick={() =>
                                                      handleSelectAllMatchesInWeek(
                                                        "week",
                                                        weekKey
                                                      )
                                                    }
                                                    title="Select all matches in this round"
                                                    disabled={
                                                      (round.matches?.length ||
                                                        0) === 0
                                                    }
                                                  >
                                                    Select All
                                                  </button>
                                                  <button
                                                    type="button"
                                                    className="week-action-btn deselect-all-btn"
                                                    onClick={() =>
                                                      handleDeselectAllMatchesInWeek(
                                                        "week",
                                                        weekKey
                                                      )
                                                    }
                                                    title="Deselect all matches in this round"
                                                    disabled={
                                                      (round.matches?.length ||
                                                        0) === 0
                                                    }
                                                  >
                                                    Deselect All
                                                  </button>
                                                </div>
                                              </div>
                                              <div className="match-list">
                                                {(round.matches?.length || 0) >
                                                0 ? (
                                                  round.matches.map((match) => {
                                                    const homeTeam =
                                                      match.participants?.find(
                                                        (p) =>
                                                          p.meta?.location ===
                                                          "home"
                                                      ) || {
                                                        name:
                                                          match.name?.split(
                                                            " vs "
                                                          )[0] || "Team A",
                                                      };
                                                    const awayTeam =
                                                      match.participants?.find(
                                                        (p) =>
                                                          p.meta?.location ===
                                                          "away"
                                                      ) || {
                                                        name:
                                                          match.name?.split(
                                                            " vs "
                                                          )[1] || "Team B",
                                                      };
                                                    const matchDate = new Date(
                                                      match.startingAt
                                                    ).toLocaleDateString(
                                                      "en-GB",
                                                      {
                                                        weekday: "long",
                                                        month: "short",
                                                        day: "numeric",
                                                      }
                                                    );
                                                    const matchTime = new Date(
                                                      match.startingAt
                                                    ).toLocaleTimeString(
                                                      "en-GB",
                                                      {
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                        hour12: false,
                                                      }
                                                    );
                                                    return (
                                                      <div
                                                        key={match._id}
                                                        className={`match-item ${
                                                          isMatchSelected(
                                                            match._id,
                                                            "weekly",
                                                            weekKey
                                                          )
                                                            ? "selected"
                                                            : ""
                                                        }`}
                                                        onClick={() =>
                                                          handleMatchSelection(
                                                            match._id,
                                                            "weekly",
                                                            weekKey
                                                          )
                                                        }
                                                      >
                                                        <div className="match-teams">
                                                          <img
                                                            src={
                                                              homeTeam?.imagePath ||
                                                              "/images/default-team.png"
                                                            }
                                                            alt={homeTeam?.name}
                                                          />
                                                          <span>
                                                            {homeTeam?.name}
                                                          </span>
                                                        </div>
                                                        <div className="match-details">
                                                          <div className="match-date">
                                                            {matchDate}
                                                          </div>
                                                          <div className="match-time">
                                                            {matchTime}
                                                          </div>
                                                        </div>
                                                        <div className="match-teams">
                                                          <span>
                                                            {awayTeam?.name}
                                                          </span>
                                                          <img
                                                            src={
                                                              awayTeam?.imagePath ||
                                                              "/images/default-team.png"
                                                            }
                                                            alt={awayTeam?.name}
                                                          />
                                                        </div>
                                                      </div>
                                                    );
                                                  })
                                                ) : (
                                                  <div className="empty-week-content">
                                                    <p>
                                                      No matches scheduled for
                                                      this round
                                                    </p>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </>
                                    ) : (
                                      <div className="no-matches">
                                        <p>
                                          No rounds available for the selected
                                          date range.
                                        </p>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          )}
                          {formik.values.timeRange === "monthly" && (
                            <div className="tab-pane active">
                              {(() => {
                                const weeks = availableMatches;
                                const weekKeys = weeks.map((w) => w.uiKey);
                                const currentWeeks =
                                  getCurrentWeekPage(weekKeys);
                                const totalPages = Math.ceil(
                                  weekKeys.length / weeksPerPage
                                );
                                return (
                                  <>
                                    <div className="tab-header">
                                      <div className="tab-header-top">
                                        <h6>Monthly Matches</h6>
                                        <span className="match-count">
                                          {weekKeys.length} rounds available
                                        </span>
                                      </div>
                                      <div className="tab-actions">
                                        <button
                                          type="button"
                                          className="tab-action-btn select-all-btn"
                                          onClick={() =>
                                            handleSelectAllMatchesInTimeRange(
                                              "monthly"
                                            )
                                          }
                                          title="Select all matches across all rounds"
                                        >
                                          Select All Rounds
                                        </button>
                                        <button
                                          type="button"
                                          className="tab-action-btn deselect-all-btn"
                                          onClick={() =>
                                            handleDeselectAllMatchesInTimeRange(
                                              "monthly"
                                            )
                                          }
                                          title="Deselect all matches across all rounds"
                                        >
                                          Deselect All Rounds
                                        </button>
                                      </div>
                                    </div>
                                    {weekKeys.length > 0 &&
                                    formik.values.startTime &&
                                    formik.values.endTime ? (
                                      <>
                                        <div className="week-tabs">
                                          <div className="week-tabs-header">
                                            <span className="week-tabs-title">
                                              Game Weeks
                                            </span>
                                            {totalPages > 1 && (
                                              <div className="week-pagination">
                                                <button
                                                  type="button"
                                                  className="week-pagination-btn"
                                                  onClick={() =>
                                                    handleWeekPageChange(
                                                      currentWeekPage - 1
                                                    )
                                                  }
                                                  disabled={
                                                    currentWeekPage === 1
                                                  }
                                                >
                                                  ‹
                                                </button>
                                                <span className="week-pagination-info">
                                                  Page {currentWeekPage} of{" "}
                                                  {totalPages}
                                                </span>
                                                <button
                                                  type="button"
                                                  className="week-pagination-btn"
                                                  onClick={() =>
                                                    handleWeekPageChange(
                                                      currentWeekPage + 1
                                                    )
                                                  }
                                                  disabled={
                                                    currentWeekPage ===
                                                    totalPages
                                                  }
                                                >
                                                  ›
                                                </button>
                                              </div>
                                            )}
                                          </div>
                                          <div className="week-tabs-grid">
                                            {currentWeeks.map((weekKey) => {
                                              const round = weeks.find(
                                                (w) => w.uiKey === weekKey
                                              );
                                              const actualRoundNumber =
                                                round.roundNumber;
                                              return (
                                                <button
                                                  type="button"
                                                  key={weekKey}
                                                  className={`week-tab ${
                                                    activeWeekTab ===
                                                    actualRoundNumber
                                                      ? "active"
                                                      : ""
                                                  } ${
                                                    (round.matches?.length ||
                                                      0) === 0
                                                      ? "empty-week"
                                                      : ""
                                                  }`}
                                                  onClick={() =>
                                                    setActiveWeekTab(
                                                      actualRoundNumber
                                                    )
                                                  }
                                                >
                                                  <span className="week-number">
                                                    Game Week{" "}
                                                    {round.roundName ||
                                                      actualRoundNumber}
                                                  </span>
                                                  <span className="week-dates">
                                                    {round.startDate.toLocaleDateString()}{" "}
                                                    -{" "}
                                                    {round.endDate.toLocaleDateString()}
                                                  </span>
                                                  {(round.matches?.length ||
                                                    0) === 0 && (
                                                    <span className="week-status">
                                                      No Matches
                                                    </span>
                                                  )}
                                                </button>
                                              );
                                            })}
                                          </div>
                                          {weekKeys.length > 12 && (
                                            <div className="week-quick-select">
                                              <span className="week-quick-select-label">
                                                Quick Jump:
                                              </span>
                                              <select
                                                className="week-quick-select-dropdown"
                                                onChange={(e) =>
                                                  handleQuickWeekSelect(
                                                    e.target.value
                                                  )
                                                }
                                                value=""
                                              >
                                                <option value="">
                                                  Select Round
                                                </option>
                                                {weeks.map((round, index) => {
                                                  const roundNumber = index + 1;
                                                  return (
                                                    <option
                                                      key={round.uiKey}
                                                      value={round.uiKey}
                                                    >
                                                      Round{" "}
                                                      {round.roundName ||
                                                        roundNumber}{" "}
                                                      (
                                                      {round.startDate.toLocaleDateString()}{" "}
                                                      -{" "}
                                                      {round.endDate.toLocaleDateString()}
                                                      )
                                                    </option>
                                                  );
                                                })}
                                              </select>
                                            </div>
                                          )}
                                        </div>
                                        {weeks.map((round, index) => {
                                          const actualRoundNumber = index + 1;
                                          const weekKey = round.uiKey;
                                          return (
                                            <div
                                              key={weekKey}
                                              className={`week-content ${
                                                weeks.length === 1 ||
                                                activeWeekTab ===
                                                  actualRoundNumber
                                                  ? "active"
                                                  : ""
                                              }`}
                                            >
                                              <div className="week-header">
                                                <div className="week-header-top">
                                                  <h6>
                                                    {weeks.length === 1
                                                      ? "Monthly Matches"
                                                      : `Game Week ${
                                                          round.roundName ||
                                                          actualRoundNumber
                                                        } Matches`}
                                                  </h6>
                                                  <span className="match-count">
                                                    {round.matches?.length || 0}{" "}
                                                    matches
                                                  </span>
                                                </div>
                                                <div className="week-actions">
                                                  <button
                                                    type="button"
                                                    className="week-action-btn select-all-btn"
                                                    onClick={() =>
                                                      handleSelectAllMatchesInWeek(
                                                        "monthly",
                                                        weekKey
                                                      )
                                                    }
                                                    title="Select all matches in this round"
                                                    disabled={
                                                      (round.matches?.length ||
                                                        0) === 0
                                                    }
                                                  >
                                                    Select All
                                                  </button>
                                                  <button
                                                    type="button"
                                                    className="week-action-btn deselect-all-btn"
                                                    onClick={() =>
                                                      handleDeselectAllMatchesInWeek(
                                                        "monthly",
                                                        weekKey
                                                      )
                                                    }
                                                    title="Deselect all matches in this round"
                                                    disabled={
                                                      (round.matches?.length ||
                                                        0) === 0
                                                    }
                                                  >
                                                    Deselect All
                                                  </button>
                                                </div>
                                              </div>
                                              <div className="match-list">
                                                {(round.matches?.length || 0) >
                                                0 ? (
                                                  round.matches.map((match) => {
                                                    const homeTeam =
                                                      match.participants?.find(
                                                        (p) =>
                                                          p.meta?.location ===
                                                          "home"
                                                      ) || {
                                                        name:
                                                          match.name?.split(
                                                            " vs "
                                                          )[0] || "Team A",
                                                      };
                                                    const awayTeam =
                                                      match.participants?.find(
                                                        (p) =>
                                                          p.meta?.location ===
                                                          "away"
                                                      ) || {
                                                        name:
                                                          match.name?.split(
                                                            " vs "
                                                          )[1] || "Team B",
                                                      };
                                                    const matchDate = new Date(
                                                      match.startingAt
                                                    ).toLocaleDateString(
                                                      "en-GB",
                                                      {
                                                        weekday: "long",
                                                        month: "short",
                                                        day: "numeric",
                                                      }
                                                    );
                                                    const matchTime = new Date(
                                                      match.startingAt
                                                    ).toLocaleTimeString(
                                                      "en-GB",
                                                      {
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                        hour12: false,
                                                      }
                                                    );
                                                    return (
                                                      <div
                                                        key={match._id}
                                                        className={`match-item ${
                                                          isMatchSelected(
                                                            match._id,
                                                            "monthly",
                                                            weekKey
                                                          )
                                                            ? "selected"
                                                            : ""
                                                        }`}
                                                        onClick={() =>
                                                          handleMatchSelection(
                                                            match._id,
                                                            "monthly",
                                                            weekKey
                                                          )
                                                        }
                                                      >
                                                        <div className="match-teams">
                                                          <img
                                                            src={
                                                              homeTeam?.imagePath ||
                                                              "/images/default-team.png"
                                                            }
                                                            alt={homeTeam?.name}
                                                          />
                                                          <span>
                                                            {homeTeam?.name}
                                                          </span>
                                                        </div>
                                                        <div className="match-details">
                                                          <div className="match-date">
                                                            {matchDate}
                                                          </div>
                                                          <div className="match-time">
                                                            {matchTime}
                                                          </div>
                                                        </div>
                                                        <div className="match-teams team2">
                                                          <span>
                                                            {awayTeam?.name}
                                                          </span>
                                                          <img
                                                            src={
                                                              awayTeam?.imagePath ||
                                                              "/images/default-team.png"
                                                            }
                                                            alt={awayTeam?.name}
                                                          />
                                                        </div>
                                                      </div>
                                                    );
                                                  })
                                                ) : (
                                                  <div className="empty-week-content">
                                                    <p>
                                                      No matches scheduled for
                                                      this round
                                                    </p>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </>
                                    ) : (
                                      <div className="no-matches">
                                        <p>
                                          No rounds available for the selected
                                          date range.
                                        </p>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          )}
                          {/* Season Matches Tab */}
                          {formik.values.timeRange === "season" && (
                            <div className="tab-pane active">
                              {(() => {
                                const weeks = availableMatches;
                                const weekKeys = weeks.map((w) => w.uiKey);
                                const currentWeeks =
                                  getCurrentWeekPage(weekKeys);
                                const totalPages = Math.ceil(
                                  weekKeys.length / weeksPerPage
                                );
                                return (
                                  <>
                                    <div className="tab-header">
                                      <div className="tab-header-top">
                                        <h6>Season Matches</h6>
                                        <span className="match-count">
                                          {weekKeys.length} rounds available
                                        </span>
                                      </div>
                                      <div className="tab-actions">
                                        <button
                                          type="button"
                                          className="tab-action-btn select-all-btn"
                                          onClick={() =>
                                            handleSelectAllMatchesInTimeRange(
                                              "season"
                                            )
                                          }
                                          title="Select all matches across all rounds"
                                        >
                                          Select All Rounds
                                        </button>
                                        <button
                                          type="button"
                                          className="tab-action-btn deselect-all-btn"
                                          onClick={() =>
                                            handleDeselectAllMatchesInTimeRange(
                                              "season"
                                            )
                                          }
                                          title="Deselect all matches across all rounds"
                                        >
                                          Deselect All Rounds
                                        </button>
                                      </div>
                                    </div>
                                    {weekKeys.length > 0 &&
                                    formik.values.startTime &&
                                    formik.values.endTime ? (
                                      <>
                                        <div className="week-tabs">
                                          <div className="week-tabs-header">
                                            <span className="week-tabs-title">
                                              Game Weeks
                                            </span>
                                            {totalPages > 1 && (
                                              <div className="week-pagination">
                                                <button
                                                  type="button"
                                                  className="week-pagination-btn"
                                                  onClick={() =>
                                                    handleWeekPageChange(
                                                      currentWeekPage - 1
                                                    )
                                                  }
                                                  disabled={
                                                    currentWeekPage === 1
                                                  }
                                                >
                                                  ‹
                                                </button>
                                                <span className="week-pagination-info">
                                                  Page {currentWeekPage} of{" "}
                                                  {totalPages}
                                                </span>
                                                <button
                                                  type="button"
                                                  className="week-pagination-btn"
                                                  onClick={() =>
                                                    handleWeekPageChange(
                                                      currentWeekPage + 1
                                                    )
                                                  }
                                                  disabled={
                                                    currentWeekPage ===
                                                    totalPages
                                                  }
                                                >
                                                  ›
                                                </button>
                                              </div>
                                            )}
                                          </div>
                                          <div className="week-tabs-grid">
                                            {currentWeeks.map((weekKey) => {
                                              const round = weeks.find(
                                                (w) => w.uiKey === weekKey
                                              );
                                              const actualRoundNumber =
                                                round.roundNumber;
                                              return (
                                                <button
                                                  type="button"
                                                  key={weekKey}
                                                  className={`week-tab ${
                                                    activeWeekTab ===
                                                    actualRoundNumber
                                                      ? "active"
                                                      : ""
                                                  } ${
                                                    (round.matches?.length ||
                                                      0) === 0
                                                      ? "empty-week"
                                                      : ""
                                                  }`}
                                                  onClick={() =>
                                                    setActiveWeekTab(
                                                      actualRoundNumber
                                                    )
                                                  }
                                                >
                                                  <span className="week-number">
                                                    Game Week{" "}
                                                    {round.roundName ||
                                                      actualRoundNumber}
                                                  </span>
                                                  <span className="week-dates">
                                                    {round.startDate.toLocaleDateString()}{" "}
                                                    -{" "}
                                                    {round.endDate.toLocaleDateString()}
                                                  </span>
                                                  {(round.matches?.length ||
                                                    0) === 0 && (
                                                    <span className="week-status">
                                                      No Matches
                                                    </span>
                                                  )}
                                                </button>
                                              );
                                            })}
                                          </div>
                                          {weekKeys.length > 12 && (
                                            <div className="week-quick-select">
                                              <span className="week-quick-select-label">
                                                Quick Jump:
                                              </span>
                                              <select
                                                className="week-quick-select-dropdown"
                                                onChange={(e) =>
                                                  handleQuickWeekSelect(
                                                    e.target.value
                                                  )
                                                }
                                                value=""
                                              >
                                                <option value="">
                                                  Select Round
                                                </option>
                                                {weeks.map((round, index) => {
                                                  const roundNumber = index + 1;
                                                  return (
                                                    <option
                                                      key={round.uiKey}
                                                      value={round.uiKey}
                                                    >
                                                      Round{" "}
                                                      {round.roundName ||
                                                        roundNumber}{" "}
                                                      (
                                                      {round.startDate.toLocaleDateString()}{" "}
                                                      -{" "}
                                                      {round.endDate.toLocaleDateString()}
                                                      )
                                                    </option>
                                                  );
                                                })}
                                              </select>
                                            </div>
                                          )}
                                        </div>
                                        {weeks.map((round, index) => {
                                          const actualRoundNumber = index + 1;
                                          const weekKey = round.uiKey;
                                          return (
                                            <div
                                              key={weekKey}
                                              className={`week-content ${
                                                weeks.length === 1 ||
                                                activeWeekTab ===
                                                  actualRoundNumber
                                                  ? "active"
                                                  : ""
                                              }`}
                                            >
                                              <div className="week-header">
                                                <div className="week-header-top">
                                                  <h6>
                                                    {weeks.length === 1
                                                      ? "Season Matches"
                                                      : `Game Week ${
                                                          round.roundName ||
                                                          actualRoundNumber
                                                        } Matches`}
                                                  </h6>
                                                  <span className="match-count">
                                                    {round.matches?.length || 0}{" "}
                                                    matches
                                                  </span>
                                                </div>
                                                <div className="week-actions">
                                                  <button
                                                    type="button"
                                                    className="week-action-btn select-all-btn"
                                                    onClick={() =>
                                                      handleSelectAllMatchesInWeek(
                                                        "season",
                                                        weekKey
                                                      )
                                                    }
                                                    title="Select all matches in this round"
                                                    disabled={
                                                      (round.matches?.length ||
                                                        0) === 0
                                                    }
                                                  >
                                                    Select All
                                                  </button>
                                                  <button
                                                    type="button"
                                                    className="week-action-btn deselect-all-btn"
                                                    onClick={() =>
                                                      handleDeselectAllMatchesInWeek(
                                                        "season",
                                                        weekKey
                                                      )
                                                    }
                                                    title="Deselect all matches in this round"
                                                    disabled={
                                                      (round.matches?.length ||
                                                        0) === 0
                                                    }
                                                  >
                                                    Deselect All
                                                  </button>
                                                </div>
                                              </div>
                                              <div className="match-list">
                                                {(round.matches?.length || 0) >
                                                0 ? (
                                                  round.matches.map((match) => {
                                                    const homeTeam =
                                                      match.participants?.find(
                                                        (p) =>
                                                          p.meta?.location ===
                                                          "home"
                                                      ) || {
                                                        name:
                                                          match.name?.split(
                                                            " vs "
                                                          )[0] || "Team A",
                                                      };
                                                    const awayTeam =
                                                      match.participants?.find(
                                                        (p) =>
                                                          p.meta?.location ===
                                                          "away"
                                                      ) || {
                                                        name:
                                                          match.name?.split(
                                                            " vs "
                                                          )[1] || "Team B",
                                                      };
                                                    const matchDate = new Date(
                                                      match.startingAt
                                                    ).toLocaleDateString(
                                                      "en-GB",
                                                      {
                                                        weekday: "long",
                                                        month: "short",
                                                        day: "numeric",
                                                      }
                                                    );
                                                    const matchTime = new Date(
                                                      match.startingAt
                                                    ).toLocaleTimeString(
                                                      "en-GB",
                                                      {
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                        hour12: false,
                                                      }
                                                    );
                                                    return (
                                                      <div
                                                        key={match._id}
                                                        className={`match-item ${
                                                          isMatchSelected(
                                                            match._id,
                                                            "season",
                                                            weekKey
                                                          )
                                                            ? "selected"
                                                            : ""
                                                        }`}
                                                        onClick={() =>
                                                          handleMatchSelection(
                                                            match._id,
                                                            "season",
                                                            weekKey
                                                          )
                                                        }
                                                      >
                                                        <div className="match-teams">
                                                          <img
                                                            src={
                                                              homeTeam?.imagePath ||
                                                              "/images/default-team.png"
                                                            }
                                                            alt={homeTeam?.name}
                                                          />
                                                          <span>
                                                            {homeTeam?.name}
                                                          </span>
                                                        </div>
                                                        <div className="match-details">
                                                          <div className="match-date">
                                                            {matchDate}
                                                          </div>
                                                          <div className="match-time">
                                                            {matchTime}
                                                          </div>
                                                        </div>
                                                        <div className="match-teams">
                                                          <span>
                                                            {awayTeam?.name}
                                                          </span>
                                                          <img
                                                            src={
                                                              awayTeam?.imagePath ||
                                                              "/images/default-team.png"
                                                            }
                                                            alt={awayTeam?.name}
                                                          />
                                                        </div>
                                                      </div>
                                                    );
                                                  })
                                                ) : (
                                                  <div className="empty-week-content">
                                                    <p>
                                                      No matches scheduled for
                                                      this round
                                                    </p>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </>
                                    ) : (
                                      <div className="no-matches">
                                        <p>
                                          No rounds available for the selected
                                          date range.
                                        </p>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      </Box>
                    </Paper>
                  )}
                {/* Game Rules */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2} mb={3}>
                    <SportsSoccer sx={{ color: "primary.main" }} />
                    <Typography variant="h6" fontWeight="600">
                      Game Type
                    </Typography>
                  </Box>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        select
                        fullWidth
                        label="Game Type"
                        name="contestType.type"
                        value={formik.values.contestType.type}
                        error={
                          formik.touched.contestType?.type &&
                          Boolean(formik.errors.contestType?.type)
                        }
                        helperText={
                          formik.touched.contestType?.type &&
                          formik.errors.contestType?.type
                        }
                        onChange={(e) => {
                          const value = e.target.value;
                          formik.setValues({
                            ...formik.values,
                            contestType: {
                              type: value,
                              playerPosition:
                                value === "position"
                                  ? formik.values.contestType.playerPosition
                                  : "",
                              actionType:
                                value === "action"
                                  ? formik.values.contestType.actionType
                                  : "",
                            },
                          });
                        }}
                        size="medium"
                      >
                        {(formik.values.timeRange !== "daily" ||
                          selectedMatches.daily.length >= 3) && [
                          <MenuItem key="standard" value="standard">
                            Standard
                          </MenuItem>,
                          <MenuItem
                            key="unlimited-budget"
                            value="unlimited-budget"
                          >
                            Unlimited Budget
                          </MenuItem>,
                          <MenuItem key="negative-point" value="negative-point">
                            Negative Points
                          </MenuItem>,
                        ]}
                        <MenuItem value="position">Position</MenuItem>
                        <MenuItem value="action">Action</MenuItem>
                      </TextField>
                    </Grid>
                    {/* {formik.values.contestType.type === "negative-point" && (
                      <Grid item xs={12}>
                        <TextField
                          select
                          fullWidth
                          label="Negative Type"
                          name="contestType.negativeType"
                          value={formik.values.contestType.negativeType}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.contestType?.negativeType &&
                            Boolean(formik.errors.contestType?.negativeType)
                          }
                          helperText={
                            formik.touched.contestType?.negativeType &&
                            formik.errors.contestType?.negativeType
                          }
                          size="medium"
                        >
                          <MenuItem value="goals">Goals</MenuItem>
                          <MenuItem value="assists">Assists</MenuItem>
                          <MenuItem value="cleanSheet">Clean Sheet</MenuItem>
                        </TextField>
                      </Grid>
                    )} */}
                    {formik.values.contestType.type === "position" && (
                      <Grid item xs={12}>
                        <TextField
                          select
                          fullWidth
                          label="Players Position"
                          name="contestType.playerPosition"
                          value={formik.values.contestType.playerPosition}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.contestType?.playerPosition &&
                            Boolean(formik.errors.contestType?.playerPosition)
                          }
                          helperText={
                            formik.touched.contestType?.playerPosition &&
                            formik.errors.contestType?.playerPosition
                          }
                          size="medium"
                        >
                          <MenuItem value="27">Forward</MenuItem>
                          <MenuItem value="26">Midfielder</MenuItem>
                          <MenuItem value="25">Defender</MenuItem>
                          <MenuItem value="24">Goalkeeper</MenuItem>
                        </TextField>
                      </Grid>
                    )}
                    {formik.values.contestType.type === "action" && (
                      <Grid item xs={12}>
                        <TextField
                          select
                          fullWidth
                          label="Action"
                          name="contestType.playerActionType"
                          value={formik.values.contestType.playerActionType}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.contestType?.playerActionType &&
                            Boolean(formik.errors.contestType?.playerActionType)
                          }
                          helperText={
                            formik.touched.contestType?.playerActionType &&
                            formik.errors.contestType?.playerActionType
                          }
                          size="medium"
                        >
                          {actions?.map((action, index) => (
                            <MenuItem key={index} value={action.value}>
                              {action.title}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
                {/* Submit Button */}
                <Box display="flex" justifyContent="center" pt={2}>
                  <LoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    loading={loading}
                    sx={{
                      py: 1.5,
                      px: 4,
                      fontSize: "1.1rem",
                      borderRadius: 2,
                      boxShadow: 2,
                      "&:hover": {
                        boxShadow: 4,
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    {id ? "Update" : "Create"} Contest
                  </LoadingButton>
                </Box>
              </Stack>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default AddEditContestManagement;